import { Directive, inject, input, TemplateRef, ViewContainerRef } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { AbilitiesSelectors } from '@recruitee/abilities-data';
import { Ability } from '@recruitee/abilities-types';
import { CurrentUserSelectors } from '@recruitee/account-data';
import { Candidate } from '@recruitee/candidate-types';
import { SubscriptionService } from '@recruitee/company-logic';
import { DevFlag, injectDevFlag } from '@recruitee/dev-flags-data';
import { EnabledIntegrationKindsSelectors } from '@recruitee/integrations-data';
import { IntegrationKinds } from '@recruitee/integrations-types';

@Directive({
  selector: '[rtKiwiHrPromoCardGuard]',
  standalone: true,
})
export class KiwiHrPromoCardGuardDirective {
  private readonly vcRef = inject(ViewContainerRef);

  private readonly templateRef = inject(TemplateRef);

  private readonly store = inject(Store);

  private readonly hasPromoteKiwiHR = injectDevFlag(DevFlag.PromoteKiwiHR);

  private readonly isTrial = inject(SubscriptionService).isTrial();

  private readonly wasClosed = this.store.selectSignal(
    CurrentUserSelectors.featureDiscovery({ feature: 'promoteKiwihrClosed' }),
  );

  private readonly canManageCandidates = this.store.selectSignal(
    AbilitiesSelectors.hasOne(Ability.ManageCandidates),
  );

  public isHired = input.required<boolean, Candidate>({
    alias: 'rtKiwiHrPromoCardGuard',
    transform: candidate => candidate.placements.some(placement => placement.hiredInThisPlacement),
  });

  private showOrHidePromo$ = combineLatest([
    toObservable(this.isHired),
    toObservable(this.wasClosed),
    toObservable(this.canManageCandidates),
    this.store.select(EnabledIntegrationKindsSelectors.hasOne(IntegrationKinds.KIWI_HR)),
  ])
    .pipe(
      map(
        ([isHired, wasClosed, canManageCandidates, isKiwiHREnabled]) =>
          isHired &&
          !wasClosed &&
          canManageCandidates &&
          !this.isTrial &&
          this.hasPromoteKiwiHR &&
          !isKiwiHREnabled,
      ),
      distinctUntilChanged(),
      takeUntilDestroyed(),
    )
    .subscribe(isHired => {
      if (isHired) {
        this.vcRef.createEmbeddedView(this.templateRef);
      } else {
        this.vcRef.clear();
      }
    });
}
